import { CdtSourceSummary } from "src/components/PlanogramRules/CdtSourceSummary";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { localeAtom, useTranslation } from "src/i18n";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts/store/hooks";
import {
  Button,
  Color,
  Flex,
  HorizontalDivider,
  Modal,
  Scroller,
  Tabs,
  Text,
  Toggle,
} from "src/elements";
import {
  DEFAULT_MIN_CANDIDATE_QUANTITY,
  DEFAULT_PALLET_RULES,
  DEFAULT_WEIGHT_RULES,
  defaultPspRules,
} from "src/pages/Merchflows_merchflowId_BasePog_basePogId_/store/types";
import {
  exportPspRulesConfig,
  parsePspRulesTemplate,
  type PspRulesFromFile,
} from "./store/configTemplate";
import { VerticalBlockingTunables, verticalBlockingPreset } from "./store/verticalBlockingPreset";
import {
  baseRulesOwnership,
  rulesOwnership,
  unheldOf,
  withoutAddedDefaults,
  withUnheld,
  type RulesOwnership,
  type UnheldRules,
} from "./store/unheldRules";
import { downloadFile } from "src/utils/downloadFile";
import { styledSafe } from "src/utils/styledComponent";
import { colorToRGBA } from "src/utils/colorToRgba";
import { pages } from "src/utils";
import { useNavigation } from "src/utils/navigation";
import {
  storePlanogramRulesDataAtom,
  StorePlanogramRulesModalBaseProps,
  StorePlanogramRulesModalProps,
  StorePlanogramRulesModalStoreProps,
} from "./store/atoms";
import { useApi } from "src/api";
import { PlanogramOptimizeOn } from "src/components";
import { GeneralPlanogramRules } from "src/components/PlanogramRules/GeneralPlanogramRules";
import { UhdPlanogramRules } from "src/components/PlanogramRules/UhdPlanogramRules";
import { OptimizationOptionsRules } from "src/components/PlanogramRules/OptimizationOptionsRules";
import { MudmapOptimizationOptions } from "src/components/PlanogramRules/MudmapOptimizationOptions";
import { WeightRules } from "src/components/PlanogramRules/WeightRules";
import { PalletRules } from "src/components/PlanogramRules/PalletRules";
import { DividerRules } from "src/components/PlanogramRules/DividerRules";
import {
  DEFAULT_DIVIDER_POLICY,
  DEFAULT_LENGTH_UNIT,
  dividerPolicyProblemFor,
  fallBackFromUnusableBase,
  readDividerPolicy,
} from "src/components/PlanogramRules/dividerRulesModel";
import { useDividerFallbackOnLoad } from "src/components/PlanogramRules/useDividerFallbackOnLoad";
import { useDividerRulesData } from "./store/useDividerRulesData";
import {
  interpretDividerRules,
  useSaveTellOShelf,
} from "src/components/PlanogramRules/tellO/tellOApi";
import { merchantSettingsAtom } from "src/components/FeatureFlagProvider/store/atoms";
import { PlanogramMudmapLevel } from "../../components/PlanogramRules/components/PlanogramMudmapLevel";
import { DividerPolicy, PspRules } from "@CommonTypes/backend/PspRules";
import { routeToUrl } from "@CommonUtils/routes";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { useUser } from "src/atoms/user";
import { withStudioWizard } from "src/pages/CdtStudio/store/atoms";
import {
  defaultVerticalBlockingModeOf,
  isVerticalBlockingWithoutRelease,
} from "src/pages/CdtStudio/store/blockingMode";
import { ConfigCopilotPanel } from "src/components/ConfigCopilot/ConfigCopilotPanel";
import type { OpenedStore } from "src/components/ConfigCopilot/store/useConfigCopilot";
import { basePogBlockingOf } from "src/components/ConfigCopilot/store/basePogBlocking";
import { merchflows_merchflowId_BasePog_basePogId_PogAtom } from "src/pages/Merchflows_merchflowId_BasePog_basePogId_/store/atoms";
import { AdvancedRules } from "./AdvancedRules";
import { OFrontDoor } from "./OFrontDoor";
import { OTouchedContext, OTouchedRow } from "src/components/PlanogramRules/components/OTouched";

const HiddenInput = styledSafe("input")`
  display: none;
`;

// The template validator does not check this field. Same test as the backend, which treats
// anything else as unset.
const parseBrandBlocksMinBays = (
  value: unknown,
): PspRules["high_density_mode"]["brand_blocks_min_bays"] =>
  value === "none" || (typeof value === "number" && Number.isFinite(value) && value >= 1)
    ? value
    : undefined;

const STORE_GENERATION_POLL_INTERVAL_MS = 3000;
const MAX_STORE_GENERATION_POLL_ATTEMPTS = 60;
type RulesTab = "GENERAL" | "UHD" | "OPTIMIZATION" | "WEIGHT";
type RulesTabOption = { label: string; value: RulesTab };

const wait = (duration: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, duration);
  });

// A bar above the tabs that says something is off and leaves the choice open.
// `isBlocking` is for the one case that does not: generation cannot start.
const Notice = ({
  title,
  text,
  isBlocking,
  testId,
  children,
}: {
  title?: string;
  text: string;
  isBlocking?: boolean;
  testId?: string;
  children: ReactNode;
}) => (
  <Flex
    background={isBlocking ? colorToRGBA(Color.yellow, 0.15) : Color.lightGray}
    border={`1px solid ${isBlocking ? Color.yellow : Color.lightGrayHover}`}
    borderRadius="3px"
    padding="10px"
    gap="10px"
    align="center"
    justify="between"
    data-testid={testId}
  >
    <Flex column gap="3px">
      {title && (
        <Text variant="small1" bold>
          {title}
        </Text>
      )}

      <Text variant="small1">{text}</Text>
    </Flex>

    <Flex gap="8px">{children}</Flex>
  </Flex>
);

export const ModalStorePlanogramRules = () => {
  const [data, setData] = useAtom(storePlanogramRulesDataAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  if (!data) return null;

  return <ModalStorePlanogramRulesInner {...data} />;
};

const ModalStorePlanogramRulesInner = (props: StorePlanogramRulesModalProps) => {
  const { merchflowId, mode } = props;
  const defaultHighDensityEnabled =
    props.mode === "store" ? props.defaultHighDensityEnabled : undefined;
  const studioBlockingState = props.mode === "store" ? props.studioBlockingState : undefined;
  const hasCdtRelease = props.mode === "store" && props.cdtReleaseId != null;
  const isCdtSourceFromVersion = props.mode === "store" && props.cdtSourceFromVersion === true;
  // A planogram finalized in CDT Studio follows that release by default, and
  // a flow started from a CDT version follows the primary / pinned CDT;
  // otherwise generation uses the CDT the data feed brought in.
  const defaultCdtSource: NonNullable<PspRules["cdt_source"]> =
    hasCdtRelease || isCdtSourceFromVersion ? "CATEGORY_TREE" : "STORE_PRODUCT";
  const cdtDefaultReason = hasCdtRelease
    ? ("release" as const)
    : isCdtSourceFromVersion
      ? ("version" as const)
      : ("dataFeed" as const);
  const { closeModal: _closeModal } = useModals();
  const { t } = useTranslation("common");
  const { toast } = useToasts();
  const { navigate } = useNavigation();
  const { user } = useUser();
  const canCreate = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_CREATE);
  const {
    postMerchflowBasePog_basePogId_StatusApi,
    postMerchflowStartStorePlanogramsGenerationApi,
    postMerchflowStartStorePlanogramsCopyToStoresGenerationApi,
    getMerchflowStoresAllApi,
    postStartMerchflowBasePogApi,
  } = useApi();

  const merchantSettings = useAtomValue(merchantSettingsAtom);
  // Same gate the backend applies: with the merchant setting off it sends no
  // weight at all, so a tab of rules nothing reads would be a lie.
  const isShelfWeightEnabled = merchantSettings?.shelfWeightEnabled ?? false;
  const [tab, setTab] = useState<RulesTab>("GENERAL");

  const [view, setView] = useState<"O" | "ADVANCED">(mode === "store" ? "O" : "ADVANCED");

  const [isOSettled, setIsOSettled] = useState(false);
  const [oConciergeReady, setOConciergeReady] = useState<boolean | null>(null);

  const [oMarks, setOMarks] = useState<Readonly<Record<string, string>>>({});
  const basePog = useAtomValue(merchflows_merchflowId_BasePog_basePogId_PogAtom);
  const basePogBlocking = useMemo(() => basePogBlockingOf(basePog), [basePog]);
  const [optimizeOn, setOptimizeOn] = useState<PspRules["optimize_on"]>("SALES");
  const [mudmapLevel, setMudmapLevel] = useState<PspRules["mudmap_level"]>(
    defaultPspRules.mudmap_level,
  );
  const [copyToStores, setCopyToStores] = useState(false);
  const [showEngineNeededNotice, setShowEngineNeededNotice] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const isGeneratingRef = useRef(false);
  const isMountedRef = useRef(true);

  const given = useRef<{ rules: object; built?: PspRules } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // GENERAL - Fixtures
  const [canAdjustShelfNotches, setCanAdjustShelfNotches] = useState(
    defaultPspRules.fixture.can_adjust_shelf_notches,
  );
  const [canAddOrDeleteShelves, setCanAddOrDeleteShelves] = useState(
    defaultPspRules.fixture.can_add_or_delete_shelves,
  );
  const [shelvesSameHeightAsBasePog, setShelvesSameHeightAsBasePog] = useState(
    defaultPspRules.fixture.shelves_same_height_as_base_pog,
  );
  const [verticalOverlap, setVerticalOverlap] = useState(defaultPspRules.fixture.vertical_overlap);
  const [mergedShelfWidthTolerancePct, setMergedShelfWidthTolerancePct] = useState(
    defaultPspRules.fixture.merged_shelf_width_tolerance_pct,
  );
  const [fingerSpaceGap, setFingerSpaceGap] = useState(defaultPspRules.fixture.finger_space ?? 0);

  // GENERAL - Range
  const [canAddNonCoreRangeProducts, setCanAddNonCoreRangeProducts] = useState(
    defaultPspRules.range.can_add_non_core_range_products,
  );
  const [canApplySqueezeToPlacedProducts, setCanApplySqueezeToPlacedProducts] = useState(
    defaultPspRules.range.can_apply_squeeze_to_placed_products,
  );
  // `min_candidate_quantity` only goes out while the switch is on. It starts off
  // unless the merchant opts in (merchant settings; 7-Eleven Malaysia does).
  const [excludeSlowMovers, setExcludeSlowMovers] = useState(
    () => merchantSettings?.excludeSlowMoversByDefault ?? false,
  );
  // Set once the planner flips the switch or a config/O sets it: from then on
  // the merchant default no longer moves it.
  const isExcludeSlowMoversChosen = useRef(false);
  const chooseExcludeSlowMovers = (value: boolean) => {
    isExcludeSlowMoversChosen.current = true;
    setExcludeSlowMovers(value);
  };
  // The modal can open before the merchant settings arrive (the Stores All
  // brand-level prompt does not wait for them), so the default is applied
  // when they do, not only on the first render.
  const excludeSlowMoversByDefault = merchantSettings?.excludeSlowMoversByDefault;
  useEffect(() => {
    if (excludeSlowMoversByDefault === undefined || isExcludeSlowMoversChosen.current) return;
    setExcludeSlowMovers(excludeSlowMoversByDefault);
  }, [excludeSlowMoversByDefault]);
  const [minCandidateQuantity, setMinCandidateQuantity] = useState(DEFAULT_MIN_CANDIDATE_QUANTITY);
  const [cdtSource, setCdtSource] = useState(defaultCdtSource);

  // GENERAL - Merchandising
  // The mode follows the studio: a release blocks the way the studio read the wall, a skipped
  // studio means horizontal. Which CDT is used is the CDT choice, not this.
  const [verticalBlockingMode, setVerticalBlockingMode] = useState(
    defaultVerticalBlockingModeOf(
      studioBlockingState,
      defaultPspRules.merchandising.vertical_blocking_mode,
    ),
  );
  const [canBreakCasesTraysIfCoreRangeNotFit, setCanBreakCasesTraysIfCoreRangeNotFit] = useState(
    defaultPspRules.merchandising.can_break_cases_trays_if_core_range_not_fit,
  );
  const [
    canFillExtraSpaceNextToCasesTraysWithLooseProducts,
    setCanFillExtraSpaceNextToCasesTraysWithLooseProducts,
  ] = useState(
    defaultPspRules.merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products,
  );
  const [
    howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
    setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
  ] = useState(
    defaultPspRules.merchandising
      .how_should_loose_products_be_placed_with_respect_to_their_cases_trays,
  );
  const [leverageAlternativeOrientations, setLeverageAlternativeOrientations] = useState<
    PspRules["merchandising"]["leverage_alternative_orientations"]
  >(defaultPspRules.merchandising.leverage_alternative_orientations);
  // Undefined keeps rules saved before these switches as they were: no stacking to the
  // shelf top, and top caps that follow zCapping.
  const [stackToShelfTop, setStackToShelfTop] = useState<boolean | undefined>(
    defaultPspRules.merchandising.stack_to_shelf_top,
  );
  const [verticalCapping, setVerticalCapping] = useState<boolean | undefined>(
    defaultPspRules.merchandising.vertical_capping,
  );
  const [stackPastMaxStack, setStackPastMaxStack] = useState<boolean | undefined>(
    defaultPspRules.merchandising.stack_past_max_stack,
  );
  const [verticalCappingPastMaxStack, setVerticalCappingPastMaxStack] = useState<
    boolean | undefined
  >(defaultPspRules.merchandising.vertical_capping_past_max_stack);
  const [extraFacingMarginalValue, setExtraFacingMarginalValue] = useState<number>(
    defaultPspRules.merchandising.extra_facing_marginal_value ?? 0,
  );
  const [snaking, setSnaking] = useState(defaultPspRules.merchandising.snaking);
  const [snakingCascadeCdtLevel, setSnakingCascadeCdtLevel] = useState(
    defaultPspRules.merchandising.snaking_cascade_cdt_level ?? null,
  );
  const [cdtStacking, setCdtStacking] = useState(defaultPspRules.merchandising.cdt_stacking);
  const [itemsSqueezeOption, setItemsSqueezeOption] = useState<
    PspRules["merchandising"]["items_squeeze_option"]
  >(defaultPspRules.merchandising.items_squeeze_option);

  // GENERAL - Dividers
  const [dividerPolicy, setDividerPolicy] = useState<DividerPolicy>(DEFAULT_DIVIDER_POLICY);
  const dividerData = useDividerRulesData(props);
  const tellOInterpret = useMemo(() => interpretDividerRules(merchflowId), [merchflowId]);
  const tellOSaveShelf = useSaveTellOShelf(merchflowId);
  useDividerFallbackOnLoad(dividerData.basePog, dividerData.isLoading, setDividerPolicy);
  // The problem is translated text, so a language change recomputes it.
  const locale = useAtomValue(localeAtom);
  const dividerProblem = useMemo(
    () =>
      dividerPolicyProblemFor(dividerPolicy, dividerData.basePog, dividerData.products, {
        libraryShelves: dividerData.libraryShelves,
        unit: DEFAULT_LENGTH_UNIT,
      }),
    [dividerPolicy, dividerData.basePog, dividerData.products, dividerData.libraryShelves, locale],
  );
  // Until the base planogram loads, the divider rule may still fall back to none.
  const isDividerRuleLoading = dividerData.isLoading;

  const dividerHold = isDividerRuleLoading
    ? t("planogramRules.dividers.readingBase")
    : dividerProblem;

  // UHD MODE - High Density Normal
  const [highDensityEnabled, setHighDensityEnabled] = useState(
    defaultHighDensityEnabled ?? defaultPspRules.high_density_mode.enabled,
  );
  const [placeAllVariants, setPlaceAllVariants] = useState(
    defaultPspRules.high_density_mode.place_all_variants,
  );
  const [snakingStrategy, setSnakingStrategy] = useState<
    PspRules["high_density_mode"]["snaking_strategy"]
  >(defaultPspRules.high_density_mode.snaking_strategy);
  const [variantContiguous, setVariantContiguous] = useState(
    defaultPspRules.high_density_mode.variant_contiguous,
  );
  const [allowBreakingVariantSequencing, setAllowBreakingVariantSequencing] = useState(
    defaultPspRules.high_density_mode.allow_breaking_variant_sequencing,
  );
  const [ameVerticalBlockingMode, setAmeVerticalBlockingMode] = useState(
    defaultVerticalBlockingModeOf(
      studioBlockingState,
      defaultPspRules.high_density_mode.vertical_blocking_mode,
    ),
  );
  // The opener keeps the release live; when the studio's answer changes, the defaults that
  // follow it move with it.
  const studioDefaultsKey = [
    defaultCdtSource,
    studioBlockingState?.kind,
    studioBlockingState?.kind === "finalized" ? studioBlockingState.releaseId : null,
    studioBlockingState?.kind === "finalized" ? studioBlockingState.wallBlocking : null,
  ].join("|");
  const studioDefaultsKeyRef = useRef(studioDefaultsKey);
  useEffect(() => {
    if (studioDefaultsKeyRef.current === studioDefaultsKey) return;
    studioDefaultsKeyRef.current = studioDefaultsKey;
    setCdtSource(defaultCdtSource);
    setVerticalBlockingMode(
      defaultVerticalBlockingModeOf(
        studioBlockingState,
        defaultPspRules.merchandising.vertical_blocking_mode,
      ),
    );
    setAmeVerticalBlockingMode(
      defaultVerticalBlockingModeOf(
        studioBlockingState,
        defaultPspRules.high_density_mode.vertical_blocking_mode,
      ),
    );
  }, [studioDefaultsKey]);
  const [strictRectangles, setStrictRectangles] = useState(
    defaultPspRules.high_density_mode.strict_rectangles,
  );
  // Preserve backend rule keys that have no frontend control when rebuilding the request.
  const [unheldRules, setUnheldRules] = useState<UnheldRules>({});

  const [brandBlocksMinBays, setBrandBlocksMinBays] =
    useState<PspRules["high_density_mode"]["brand_blocks_min_bays"]>();
  const [verticalSplitSection, setVerticalSplitSection] = useState(
    defaultPspRules.fixture.vertical_split_section,
  );

  // UHD MODE - UHD Building Blocks
  // High Density Building Blocks Mode
  const [buildingBlocksEnabled, setBuildingBlocksEnabled] = useState(
    defaultPspRules.high_density_building_blocks_mode.enabled,
  );
  const [buildingBlocksDirection, setBuildingBlocksDirection] = useState<
    PspRules["high_density_building_blocks_mode"]["direction"]
  >(defaultPspRules.high_density_building_blocks_mode.direction);
  const [startFromType, setStartFromType] = useState<
    PspRules["high_density_building_blocks_mode"]["start_from"]
  >(defaultPspRules.high_density_building_blocks_mode.start_from);

  // OPTIMIZATION OPTIONS
  const [spaceMix, setSpaceMix] = useState(defaultPspRules.relax_factors.space_mix);
  const [eitherOr, setEitherOr] = useState(defaultPspRules.relax_factors.either_or);
  const [bothOrNone, setBothOrNone] = useState(defaultPspRules.relax_factors.both_or_none);
  const [preReq, setPreReq] = useState(defaultPspRules.relax_factors.prereq);
  const [shelfItemCount, setShelfItemCount] = useState(
    defaultPspRules.relax_factors.shelf_item_count,
  );
  const [fixedNotchNum, setFixedNotchNum] = useState(defaultPspRules.relax_factors.fixed_notch_num);
  const [fingerSpace, setFingerSpace] = useState(defaultPspRules.relax_factors.finger_space);
  const [unitsNotEnoughDos, setUnitsNotEnoughDos] = useState(
    defaultPspRules.relax_factors.units_not_enough_dos,
  );
  const [unitsNotEnoughMos, setUnitsNotEnoughMos] = useState(
    defaultPspRules.relax_factors.units_not_enough_mos,
  );
  const [unitsTooMany, setUnitsTooMany] = useState(defaultPspRules.relax_factors.units_too_many);
  const [facingsNotEnough, setFacingsNotEnough] = useState(
    defaultPspRules.relax_factors.facings_not_enough,
  );
  const [facingsTooMany, setFacingsTooMany] = useState(
    defaultPspRules.relax_factors.facings_too_many,
  );
  const [rowsNotEnough, setRowsNotEnough] = useState(defaultPspRules.relax_factors.rows_not_enough);
  const [minWidth, setMinWidth] = useState(defaultPspRules.relax_factors.min_width);
  const [minDistribution, setMinDistribution] = useState(
    defaultPspRules.relax_factors.min_distribution,
  );
  const [widthSlack, setWidthSlack] = useState(defaultPspRules.relax_factors.width_slack);
  const [cdt1Contiguous, setCdt1Contiguous] = useState(
    defaultPspRules.relax_factors.cdt1_contiguous,
  );
  const [cdt2Contiguous, setCdt2Contiguous] = useState(
    defaultPspRules.relax_factors.cdt2_contiguous,
  );
  const [cdt3Contiguous, setCdt3Contiguous] = useState(
    defaultPspRules.relax_factors.cdt3_contiguous,
  );
  const [cdt1Order, setCdt1Order] = useState(defaultPspRules.relax_factors.cdt1_order);
  const [cdt2Order, setCdt2Order] = useState(defaultPspRules.relax_factors.cdt2_order);
  const [cdt3Order, setCdt3Order] = useState(defaultPspRules.relax_factors.cdt3_order);
  const [itemIdOrder, setItemIdOrder] = useState(defaultPspRules.relax_factors.item_id_order);
  const [itemSpan, setItemSpan] = useState(defaultPspRules.relax_factors.item_span);
  const [cdt1Span, setCdt1Span] = useState(defaultPspRules.relax_factors.cdt1_span);
  const [cdt2Span, setCdt2Span] = useState(defaultPspRules.relax_factors.cdt2_span);
  const [cdt3Span, setCdt3Span] = useState(defaultPspRules.relax_factors.cdt3_span);
  const [secondaryOrientation, setSecondaryOrientation] = useState(
    defaultPspRules.relax_factors.secondary_orientation,
  );
  const [shelfSegmentSpace, setShelfSegmentSpace] = useState(
    defaultPspRules.relax_factors.shelf_segment_space,
  );

  // WEIGHT RULES
  const [defaultShelfWeightLimit, setDefaultShelfWeightLimit] = useState(
    DEFAULT_WEIGHT_RULES.default_shelf_weight_limit,
  );
  // Keep the draft above WeightRules because switching tabs unmounts that component.

  const [draftShelfCapGrams, setDraftShelfCapGrams] = useState(
    DEFAULT_WEIGHT_RULES.default_shelf_weight_limit ?? 0,
  );
  // Null cannot distinguish an off cap from an enabled cap awaiting its value.

  const [shelfCapOn, setShelfCapOn] = useState(
    DEFAULT_WEIGHT_RULES.default_shelf_weight_limit !== null,
  );
  const [heavyRule, setHeavyRule] = useState(DEFAULT_WEIGHT_RULES.heavy_rule);
  const [resolveOverweightShelves, setResolveOverweightShelves] = useState(
    DEFAULT_WEIGHT_RULES.resolve_overweight_shelves,
  );
  const [swapHeavyBrandsBelowMaxHeight, setSwapHeavyBrandsBelowMaxHeight] = useState(
    DEFAULT_WEIGHT_RULES.swap_heavy_brands_below_max_height,
  );
  const [swapFillFloorPct, setSwapFillFloorPct] = useState(
    DEFAULT_WEIGHT_RULES.swap_fill_floor_pct,
  );

  // PALLETS
  const [maxProductsPerPallet, setMaxProductsPerPallet] = useState(
    DEFAULT_PALLET_RULES.max_products_per_pallet,
  );
  const [maxStackHeight, setMaxStackHeight] = useState(DEFAULT_PALLET_RULES.max_stack_height);

  // MUDMAP OPTIMIZATION OPTIONS
  const mudmapPenaltyDefaults: NonNullable<PspRules["mudmap_generation_penalty_multiplier"]> =
    defaultPspRules.mudmap_generation_penalty_multiplier ?? {
      totalSpaceSlack: 0,
      cdt0SectionsSpanning: 0,
      cdt0ShelvesSpanning: 0,
      cdt2SectionsSpanning: 0,
      cdt2ShelvesSpanning: 0,
      cdt0RoundingSlack: 0,
      cdt2RoundingSlack: 0,
      cdt1BreakingSequencing: 0,
      cdt2BreakingSequencing: 0,
      cdtSpaceMixSlack: 0,
      cdt0ShelvesMisaligned: 0,
      itemWidthSatisfied: 0,
      cdt0ShelfSpaceMisalignment: 0,
      cdt2ShelfSpaceMisalignment: 0,
      verticalBlockShelfPresence: 0,
      cdt0CellXMisalignment: 0,
      cdt2CellXMisalignment: 0,
    };

  const [totalSpaceSlack, setTotalSpaceSlack] = useState(mudmapPenaltyDefaults.totalSpaceSlack);
  const [cdt0SectionsSpanning, setCdt0SectionsSpanning] = useState(
    mudmapPenaltyDefaults.cdt0SectionsSpanning,
  );
  const [cdt0ShelvesSpanning, setCdt0ShelvesSpanning] = useState(
    mudmapPenaltyDefaults.cdt0ShelvesSpanning,
  );
  const [cdt2SectionsSpanning, setCdt2SectionsSpanning] = useState(
    mudmapPenaltyDefaults.cdt2SectionsSpanning,
  );
  const [cdt2ShelvesSpanning, setCdt2ShelvesSpanning] = useState(
    mudmapPenaltyDefaults.cdt2ShelvesSpanning,
  );
  const [cdt0RoundingSlack, setCdt0RoundingSlack] = useState(
    mudmapPenaltyDefaults.cdt0RoundingSlack,
  );
  const [cdt2RoundingSlack, setCdt2RoundingSlack] = useState(
    mudmapPenaltyDefaults.cdt2RoundingSlack,
  );
  const [cdt1BreakingSequencing, setCdt1BreakingSequencing] = useState(
    mudmapPenaltyDefaults.cdt1BreakingSequencing,
  );
  const [cdt2BreakingSequencing, setCdt2BreakingSequencing] = useState(
    mudmapPenaltyDefaults.cdt2BreakingSequencing,
  );
  const [cdtSpaceMixSlack, setCdtSpaceMixSlack] = useState(mudmapPenaltyDefaults.cdtSpaceMixSlack);
  const [cdt0ShelvesMisaligned, setCdt0ShelvesMisaligned] = useState(
    mudmapPenaltyDefaults.cdt0ShelvesMisaligned,
  );
  const [itemWidthSatisfied, setItemWidthSatisfied] = useState(
    mudmapPenaltyDefaults.itemWidthSatisfied,
  );
  const [cdt0ShelfSpaceMisalignment, setCdt0ShelfSpaceMisalignment] = useState(
    mudmapPenaltyDefaults.cdt0ShelfSpaceMisalignment,
  );
  const [cdt2ShelfSpaceMisalignment, setCdt2ShelfSpaceMisalignment] = useState(
    mudmapPenaltyDefaults.cdt2ShelfSpaceMisalignment,
  );
  const [verticalBlockShelfPresence, setVerticalBlockShelfPresence] = useState(
    mudmapPenaltyDefaults.verticalBlockShelfPresence,
  );
  const [cdt0CellXMisalignment, setCdt0CellXMisalignment] = useState(
    mudmapPenaltyDefaults.cdt0CellXMisalignment,
  );
  const [cdt2CellXMisalignment, setCdt2CellXMisalignment] = useState(
    mudmapPenaltyDefaults.cdt2CellXMisalignment,
  );

  const resetMudmapOptimizationOptions = () => {
    setTotalSpaceSlack(mudmapPenaltyDefaults.totalSpaceSlack);
    setCdt0SectionsSpanning(mudmapPenaltyDefaults.cdt0SectionsSpanning);
    setCdt0ShelvesSpanning(mudmapPenaltyDefaults.cdt0ShelvesSpanning);
    setCdt2SectionsSpanning(mudmapPenaltyDefaults.cdt2SectionsSpanning);
    setCdt2ShelvesSpanning(mudmapPenaltyDefaults.cdt2ShelvesSpanning);
    setCdt0RoundingSlack(mudmapPenaltyDefaults.cdt0RoundingSlack);
    setCdt2RoundingSlack(mudmapPenaltyDefaults.cdt2RoundingSlack);
    setCdt1BreakingSequencing(mudmapPenaltyDefaults.cdt1BreakingSequencing);
    setCdt2BreakingSequencing(mudmapPenaltyDefaults.cdt2BreakingSequencing);
    setCdtSpaceMixSlack(mudmapPenaltyDefaults.cdtSpaceMixSlack);
    setCdt0ShelvesMisaligned(mudmapPenaltyDefaults.cdt0ShelvesMisaligned);
    setItemWidthSatisfied(mudmapPenaltyDefaults.itemWidthSatisfied);
    setCdt0ShelfSpaceMisalignment(mudmapPenaltyDefaults.cdt0ShelfSpaceMisalignment);
    setCdt2ShelfSpaceMisalignment(mudmapPenaltyDefaults.cdt2ShelfSpaceMisalignment);
    setVerticalBlockShelfPresence(mudmapPenaltyDefaults.verticalBlockShelfPresence);
    setCdt0CellXMisalignment(mudmapPenaltyDefaults.cdt0CellXMisalignment);
    setCdt2CellXMisalignment(mudmapPenaltyDefaults.cdt2CellXMisalignment);
  };

  // The preset is a starting point: every value stays editable afterwards.
  const applyVerticalBlockingTunables = (tunables: VerticalBlockingTunables) => {
    setMudmapLevel(tunables.mudmapLevel);
    setExtraFacingMarginalValue(tunables.extraFacingMarginalValue);
    setPlaceAllVariants(tunables.placeAllVariants);
    setSnakingStrategy(tunables.snakingStrategy);
    setSnaking(tunables.snaking);
    setCdtStacking(tunables.cdtStacking);
    setFixedNotchNum(tunables.fixedNotchNum);
    setSpaceMix(tunables.spaceMix);
    setUnitsNotEnoughDos(tunables.unitsNotEnoughDos);
    setUnitsNotEnoughMos(tunables.unitsNotEnoughMos);
    setFacingsTooMany(tunables.facingsTooMany);
    setMinDistribution(tunables.minDistribution);
    setShelfSegmentSpace(tunables.shelfSegmentSpace);
    setCdt1Contiguous(tunables.cdt1Contiguous);
    setCdt2Contiguous(tunables.cdt2Contiguous);
    setCdt3Contiguous(tunables.cdt3Contiguous);
    setCdt1Order(tunables.cdt1Order);
    setCdt2Order(tunables.cdt2Order);
    setCdt3Order(tunables.cdt3Order);
    setItemIdOrder(tunables.itemIdOrder);
    setItemSpan(tunables.itemSpan);
    setCdt0ShelvesMisaligned(tunables.cdt0ShelvesMisaligned);
    setCdt0ShelfSpaceMisalignment(tunables.cdt0ShelfSpaceMisalignment);
    setCdt2ShelfSpaceMisalignment(tunables.cdt2ShelfSpaceMisalignment);
    setCdt0CellXMisalignment(tunables.cdt0CellXMisalignment);
    setCdt2CellXMisalignment(tunables.cdt2CellXMisalignment);
  };

  // One switch for base and store generation alike: the core flag, the mudmap
  // flag, the tested preset and the engine. Base generation posts the same
  // psp_rules as store generation, so a core-only switch there would send
  // vertical blocking with the production defaults it was never tested with.
  const handleVerticalBlockingModeChange = (isOn: boolean) => {
    setVerticalBlockingMode(isOn);
    setAmeVerticalBlockingMode(isOn);
    if (isOn) {
      applyVerticalBlockingTunables(verticalBlockingPreset);
      setHighDensityEnabled(true);
    }

    setShowEngineNeededNotice(false);
  };

  // Vertical blocking remains enabled when Auto-Mudmap is off, so leave the user
  // an informational choice without blocking generation.
  const handleHighDensityEnabledChange = (isOn: boolean) => {
    setHighDensityEnabled(isOn);
    setShowEngineNeededNotice(!isOn && (verticalBlockingMode || ameVerticalBlockingMode));
  };

  const storeOwnership: RulesOwnership<
    ReturnType<typeof buildHeldRules>,
    typeof rulesOwnership
  > = rulesOwnership;

  const applyPspRules = (rules: PspRulesFromFile) => {
    given.current = { rules };

    setUnheldRules(unheldOf(rules, mode === "base" ? baseRulesOwnership : storeOwnership));
    // The template validator does not check this field, so narrow rather than
    // trust it: a hand-edited config could carry any JSON value. A config
    // without one keeps the default this planogram would get.
    setCdtSource(
      rules.cdt_source === "CATEGORY_TREE" || rules.cdt_source === "STORE_PRODUCT"
        ? rules.cdt_source
        : defaultCdtSource,
    );

    const importedVerticalBlockingMode =
      rules.merchandising.vertical_blocking_mode ??
      defaultPspRules.merchandising.vertical_blocking_mode;
    const importedAmeVerticalBlockingMode =
      rules.high_density_mode.vertical_blocking_mode ??
      defaultPspRules.high_density_mode.vertical_blocking_mode;

    // `default_shelf_weight_limit` and `heavy_rule` are both meaningfully
    // nullable: `?? default` would resurrect a cap or a rule the imported
    // config deliberately turned off.
    const importedShelfCap = rules.weight_rules
      ? rules.weight_rules.default_shelf_weight_limit
      : DEFAULT_WEIGHT_RULES.default_shelf_weight_limit;
    setDefaultShelfWeightLimit(importedShelfCap);
    setShelfCapOn(importedShelfCap !== null);
    // Clear the draft on a no-cap import so re-enabling cannot restore the previous cap.

    setDraftShelfCapGrams(importedShelfCap ?? 0);
    setHeavyRule(
      rules.weight_rules ? rules.weight_rules.heavy_rule : DEFAULT_WEIGHT_RULES.heavy_rule,
    );
    setResolveOverweightShelves(
      rules.weight_rules?.resolve_overweight_shelves ??
        DEFAULT_WEIGHT_RULES.resolve_overweight_shelves,
    );
    setSwapHeavyBrandsBelowMaxHeight(
      rules.weight_rules?.swap_heavy_brands_below_max_height ??
        DEFAULT_WEIGHT_RULES.swap_heavy_brands_below_max_height,
    );
    setSwapFillFloorPct(
      rules.weight_rules?.swap_fill_floor_pct ?? DEFAULT_WEIGHT_RULES.swap_fill_floor_pct,
    );
    // `max_stack_height` is meaningfully nullable (each pallet's own height),
    // so a config that cleared it keeps it cleared.
    setMaxProductsPerPallet(
      rules.pallets?.max_products_per_pallet ?? DEFAULT_PALLET_RULES.max_products_per_pallet,
    );
    setMaxStackHeight(
      rules.pallets ? rules.pallets.max_stack_height : DEFAULT_PALLET_RULES.max_stack_height,
    );
    setCanAdjustShelfNotches(
      rules.fixture.can_adjust_shelf_notches ?? defaultPspRules.fixture.can_adjust_shelf_notches,
    );
    setCanAddOrDeleteShelves(
      rules.fixture.can_add_or_delete_shelves ?? defaultPspRules.fixture.can_add_or_delete_shelves,
    );
    setShelvesSameHeightAsBasePog(
      rules.fixture.shelves_same_height_as_base_pog ??
        defaultPspRules.fixture.shelves_same_height_as_base_pog,
    );
    setVerticalOverlap(rules.fixture.vertical_overlap ?? defaultPspRules.fixture.vertical_overlap);
    setMergedShelfWidthTolerancePct(
      rules.fixture.merged_shelf_width_tolerance_pct ??
        defaultPspRules.fixture.merged_shelf_width_tolerance_pct,
    );
    setVerticalSplitSection(
      rules.fixture.vertical_split_section ?? defaultPspRules.fixture.vertical_split_section,
    );
    setFingerSpaceGap(rules.fixture.finger_space ?? 0);

    setCanAddNonCoreRangeProducts(
      rules.range.can_add_non_core_range_products ??
        defaultPspRules.range.can_add_non_core_range_products,
    );
    setCanApplySqueezeToPlacedProducts(
      rules.range.can_apply_squeeze_to_placed_products ??
        defaultPspRules.range.can_apply_squeeze_to_placed_products,
    );
    // A floor the input itself would accept (a finite number, 0 or more) turns
    // the switch on. Absent, null, or one the input would refuse (negative: no
    // product sells below it, so the "on" switch would filter nothing) leaves it
    // off and keeps the seed, as a bad `cdt_source` falls back above.
    const importedMinCandidateQuantity = rules.range.min_candidate_quantity;
    const hasMinCandidateQuantity =
      typeof importedMinCandidateQuantity === "number" &&
      Number.isFinite(importedMinCandidateQuantity) &&
      importedMinCandidateQuantity >= 0;
    // The config decides, whatever the merchant default says.
    chooseExcludeSlowMovers(hasMinCandidateQuantity);
    setMinCandidateQuantity(
      hasMinCandidateQuantity ? importedMinCandidateQuantity : DEFAULT_MIN_CANDIDATE_QUANTITY,
    );

    setVerticalBlockingMode(importedVerticalBlockingMode);
    setCanBreakCasesTraysIfCoreRangeNotFit(
      rules.merchandising.can_break_cases_trays_if_core_range_not_fit ??
        defaultPspRules.merchandising.can_break_cases_trays_if_core_range_not_fit,
    );
    setCanFillExtraSpaceNextToCasesTraysWithLooseProducts(
      rules.merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products ??
        defaultPspRules.merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products,
    );
    setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays(
      rules.merchandising.how_should_loose_products_be_placed_with_respect_to_their_cases_trays ??
        defaultPspRules.merchandising
          .how_should_loose_products_be_placed_with_respect_to_their_cases_trays,
    );
    setLeverageAlternativeOrientations(
      rules.merchandising.leverage_alternative_orientations ??
        defaultPspRules.merchandising.leverage_alternative_orientations,
    );
    setStackToShelfTop(rules.merchandising.stack_to_shelf_top);
    setVerticalCapping(rules.merchandising.vertical_capping);
    setStackPastMaxStack(rules.merchandising.stack_past_max_stack);
    setVerticalCappingPastMaxStack(rules.merchandising.vertical_capping_past_max_stack);
    setExtraFacingMarginalValue(
      rules.merchandising.extra_facing_marginal_value ??
        defaultPspRules.merchandising.extra_facing_marginal_value ??
        0,
    );
    setSnaking(rules.merchandising.snaking ?? defaultPspRules.merchandising.snaking);
    setSnakingCascadeCdtLevel(rules.merchandising.snaking_cascade_cdt_level ?? null);
    setCdtStacking(rules.merchandising.cdt_stacking ?? defaultPspRules.merchandising.cdt_stacking);
    setItemsSqueezeOption(
      rules.merchandising.items_squeeze_option ??
        defaultPspRules.merchandising.items_squeeze_option,
    );
    // A template from before dividers existed gets the default rule. Like on load, a base
    // rule on unusable base divider data falls back to none (once, here).
    setDividerPolicy(
      fallBackFromUnusableBase(
        readDividerPolicy(rules.merchandising.dividers),
        dividerData.basePog,
      ),
    );

    // Building blocks is an additional mode on top of the engine, so configs
    // with building blocks enabled must keep the engine enabled as well.
    const engineEnabled =
      (rules.high_density_mode.enabled ?? defaultPspRules.high_density_mode.enabled) ||
      (rules.high_density_building_blocks_mode.enabled ??
        defaultPspRules.high_density_building_blocks_mode.enabled);
    setHighDensityEnabled(engineEnabled);
    setPlaceAllVariants(
      rules.high_density_mode.place_all_variants ??
        defaultPspRules.high_density_mode.place_all_variants,
    );
    setSnakingStrategy(
      rules.high_density_mode.snaking_strategy ??
        defaultPspRules.high_density_mode.snaking_strategy,
    );
    setVariantContiguous(
      rules.high_density_mode.variant_contiguous ??
        defaultPspRules.high_density_mode.variant_contiguous,
    );
    setAllowBreakingVariantSequencing(
      rules.high_density_mode.allow_breaking_variant_sequencing ??
        defaultPspRules.high_density_mode.allow_breaking_variant_sequencing,
    );
    setAmeVerticalBlockingMode(importedAmeVerticalBlockingMode);
    setStrictRectangles(
      rules.high_density_mode.strict_rectangles ??
        defaultPspRules.high_density_mode.strict_rectangles,
    );
    setBrandBlocksMinBays(parseBrandBlocksMinBays(rules.high_density_mode.brand_blocks_min_bays));

    setBuildingBlocksEnabled(
      rules.high_density_building_blocks_mode.enabled ??
        defaultPspRules.high_density_building_blocks_mode.enabled,
    );
    setBuildingBlocksDirection(
      rules.high_density_building_blocks_mode.direction ??
        defaultPspRules.high_density_building_blocks_mode.direction,
    );
    setStartFromType(
      rules.high_density_building_blocks_mode.start_from ??
        defaultPspRules.high_density_building_blocks_mode.start_from,
    );

    setMudmapLevel(rules.mudmap_level ?? defaultPspRules.mudmap_level);
    setOptimizeOn(rules.optimize_on ?? defaultPspRules.optimize_on);

    setSpaceMix(rules.relax_factors.space_mix ?? defaultPspRules.relax_factors.space_mix);
    setEitherOr(rules.relax_factors.either_or ?? defaultPspRules.relax_factors.either_or);
    setBothOrNone(rules.relax_factors.both_or_none ?? defaultPspRules.relax_factors.both_or_none);
    setPreReq(rules.relax_factors.prereq ?? defaultPspRules.relax_factors.prereq);
    setShelfItemCount(
      rules.relax_factors.shelf_item_count ?? defaultPspRules.relax_factors.shelf_item_count,
    );
    setFixedNotchNum(
      rules.relax_factors.fixed_notch_num ?? defaultPspRules.relax_factors.fixed_notch_num,
    );
    setFingerSpace(rules.relax_factors.finger_space ?? defaultPspRules.relax_factors.finger_space);
    setUnitsNotEnoughDos(
      rules.relax_factors.units_not_enough_dos ??
        defaultPspRules.relax_factors.units_not_enough_dos,
    );
    setUnitsNotEnoughMos(
      rules.relax_factors.units_not_enough_mos ??
        defaultPspRules.relax_factors.units_not_enough_mos,
    );
    setUnitsTooMany(
      rules.relax_factors.units_too_many ?? defaultPspRules.relax_factors.units_too_many,
    );
    setFacingsNotEnough(
      rules.relax_factors.facings_not_enough ?? defaultPspRules.relax_factors.facings_not_enough,
    );
    setFacingsTooMany(
      rules.relax_factors.facings_too_many ?? defaultPspRules.relax_factors.facings_too_many,
    );
    setRowsNotEnough(
      rules.relax_factors.rows_not_enough ?? defaultPspRules.relax_factors.rows_not_enough,
    );
    setMinWidth(rules.relax_factors.min_width ?? defaultPspRules.relax_factors.min_width);
    setMinDistribution(
      rules.relax_factors.min_distribution ?? defaultPspRules.relax_factors.min_distribution,
    );
    setWidthSlack(rules.relax_factors.width_slack ?? defaultPspRules.relax_factors.width_slack);
    setCdt1Contiguous(
      rules.relax_factors.cdt1_contiguous ?? defaultPspRules.relax_factors.cdt1_contiguous,
    );
    setCdt2Contiguous(
      rules.relax_factors.cdt2_contiguous ?? defaultPspRules.relax_factors.cdt2_contiguous,
    );
    setCdt3Contiguous(
      rules.relax_factors.cdt3_contiguous ?? defaultPspRules.relax_factors.cdt3_contiguous,
    );
    setCdt1Order(rules.relax_factors.cdt1_order ?? defaultPspRules.relax_factors.cdt1_order);
    setCdt2Order(rules.relax_factors.cdt2_order ?? defaultPspRules.relax_factors.cdt2_order);
    setCdt3Order(rules.relax_factors.cdt3_order ?? defaultPspRules.relax_factors.cdt3_order);
    setItemIdOrder(
      rules.relax_factors.item_id_order ?? defaultPspRules.relax_factors.item_id_order,
    );
    setItemSpan(rules.relax_factors.item_span ?? defaultPspRules.relax_factors.item_span);
    setCdt1Span(rules.relax_factors.cdt1_span ?? defaultPspRules.relax_factors.cdt1_span);
    setCdt2Span(rules.relax_factors.cdt2_span ?? defaultPspRules.relax_factors.cdt2_span);
    setCdt3Span(rules.relax_factors.cdt3_span ?? defaultPspRules.relax_factors.cdt3_span);
    setSecondaryOrientation(
      rules.relax_factors.secondary_orientation ??
        defaultPspRules.relax_factors.secondary_orientation,
    );
    setShelfSegmentSpace(
      rules.relax_factors.shelf_segment_space ?? defaultPspRules.relax_factors.shelf_segment_space,
    );

    const penalty = rules.mudmap_generation_penalty_multiplier ?? mudmapPenaltyDefaults;
    setTotalSpaceSlack(penalty.totalSpaceSlack ?? mudmapPenaltyDefaults.totalSpaceSlack);
    setCdt0SectionsSpanning(
      penalty.cdt0SectionsSpanning ?? mudmapPenaltyDefaults.cdt0SectionsSpanning,
    );
    setCdt0ShelvesSpanning(
      penalty.cdt0ShelvesSpanning ?? mudmapPenaltyDefaults.cdt0ShelvesSpanning,
    );
    setCdt2SectionsSpanning(
      penalty.cdt2SectionsSpanning ?? mudmapPenaltyDefaults.cdt2SectionsSpanning,
    );
    setCdt2ShelvesSpanning(
      penalty.cdt2ShelvesSpanning ?? mudmapPenaltyDefaults.cdt2ShelvesSpanning,
    );
    setCdt0RoundingSlack(penalty.cdt0RoundingSlack ?? mudmapPenaltyDefaults.cdt0RoundingSlack);
    setCdt2RoundingSlack(penalty.cdt2RoundingSlack ?? mudmapPenaltyDefaults.cdt2RoundingSlack);
    setCdt1BreakingSequencing(
      penalty.cdt1BreakingSequencing ?? mudmapPenaltyDefaults.cdt1BreakingSequencing,
    );
    setCdt2BreakingSequencing(
      penalty.cdt2BreakingSequencing ?? mudmapPenaltyDefaults.cdt2BreakingSequencing,
    );
    setCdtSpaceMixSlack(penalty.cdtSpaceMixSlack ?? mudmapPenaltyDefaults.cdtSpaceMixSlack);
    setCdt0ShelvesMisaligned(
      penalty.cdt0ShelvesMisaligned ?? mudmapPenaltyDefaults.cdt0ShelvesMisaligned,
    );
    setItemWidthSatisfied(penalty.itemWidthSatisfied ?? mudmapPenaltyDefaults.itemWidthSatisfied);
    setCdt0ShelfSpaceMisalignment(
      penalty.cdt0ShelfSpaceMisalignment ?? mudmapPenaltyDefaults.cdt0ShelfSpaceMisalignment,
    );
    setCdt2ShelfSpaceMisalignment(
      penalty.cdt2ShelfSpaceMisalignment ?? mudmapPenaltyDefaults.cdt2ShelfSpaceMisalignment,
    );
    setVerticalBlockShelfPresence(
      penalty.verticalBlockShelfPresence ?? mudmapPenaltyDefaults.verticalBlockShelfPresence,
    );
    setCdt0CellXMisalignment(
      penalty.cdt0CellXMisalignment ?? mudmapPenaltyDefaults.cdt0CellXMisalignment,
    );
    setCdt2CellXMisalignment(
      penalty.cdt2CellXMisalignment ?? mudmapPenaltyDefaults.cdt2CellXMisalignment,
    );

    setShowEngineNeededNotice(
      mode === "store" &&
        (importedVerticalBlockingMode || importedAmeVerticalBlockingMode) &&
        !engineEnabled,
    );
  };

  const handleExportConfig = () => {
    downloadFile({
      name: "planogram-config",
      extension: "json",
      data: exportPspRulesConfig(buildPspRules()),
    });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportConfig = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const rules = await parsePspRulesTemplate(file);
      applyPspRules(rules);
      toast({ title: "Configuration imported successfully" });
    } catch (error) {
      toast({ title: "Invalid configuration file", error });
    }
  };

  const buildPspRules = (): PspRules => withUnheld(buildHeldRules(), unheldRules);

  // Every field the modal owns, including the ones `buildHeldRules` leaves out
  // while their switch is off: an imported floor must not be stashed as unheld,
  // or it would come back through `withUnheld` after the switch is turned off.
  const rulesHeldByModal = (): PspRules => {
    const held = buildHeldRules();
    return {
      ...held,
      range: { ...held.range, min_candidate_quantity: null },
      // The stacking and top-cap rules too, whether or not they are sent
      merchandising: {
        stack_to_shelf_top: undefined,
        vertical_capping: undefined,
        stack_past_max_stack: undefined,
        vertical_capping_past_max_stack: undefined,
        ...held.merchandising,
      },
    };
  };

  // O must not mistake defaults added by this modal for settings supplied by the user.
  const buildRulesForO = (): PspRules => {
    const { rules, built } = given.current ?? {};
    return rules && built ? withoutAddedDefaults(buildPspRules(), rules, built) : buildPspRules();
  };

  useEffect(() => {
    if (given.current && !given.current.built) given.current.built = buildPspRules();
  });

  const buildHeldRules = () => {
    return {
      fixture: {
        can_adjust_shelf_notches: canAdjustShelfNotches,
        can_add_or_delete_shelves: canAddOrDeleteShelves,
        shelves_same_height_as_base_pog: shelvesSameHeightAsBasePog,
        vertical_overlap: verticalOverlap,
        merged_shelf_width_tolerance_pct: mergedShelfWidthTolerancePct,
        vertical_split_section: verticalSplitSection,
        finger_space: fingerSpaceGap,
      },
      range: {
        can_add_non_core_range_products: canAddNonCoreRangeProducts,
        can_apply_squeeze_to_placed_products: canApplySqueezeToPlacedProducts,
        ...(excludeSlowMovers && { min_candidate_quantity: minCandidateQuantity }),
      },
      cdt_source: cdtSource,
      merchandising: {
        vertical_blocking_mode: verticalBlockingMode,
        can_break_cases_trays_if_core_range_not_fit: canBreakCasesTraysIfCoreRangeNotFit,
        can_fill_extra_space_next_to_cases_trays_with_loose_products:
          canFillExtraSpaceNextToCasesTraysWithLooseProducts,
        how_should_loose_products_be_placed_with_respect_to_their_cases_trays:
          howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
        leverage_alternative_orientations: leverageAlternativeOrientations,
        ...(stackToShelfTop !== undefined && {
          stack_to_shelf_top: stackToShelfTop,
        }),
        ...(verticalCapping !== undefined && { vertical_capping: verticalCapping }),
        // Hidden under a parent switched off, so not sent: the planner cannot see it.
        ...(stackToShelfTop &&
          stackPastMaxStack !== undefined && { stack_past_max_stack: stackPastMaxStack }),
        ...(verticalCapping &&
          verticalCappingPastMaxStack !== undefined && {
            vertical_capping_past_max_stack: verticalCappingPastMaxStack,
          }),
        extra_facing_marginal_value: extraFacingMarginalValue ?? 0,
        snaking: snaking,
        snaking_cascade_cdt_level: snakingCascadeCdtLevel ?? undefined,
        cdt_stacking: cdtStacking,
        items_squeeze_option: itemsSqueezeOption,
        dividers: dividerPolicy,
      },
      high_density_mode: {
        enabled: highDensityEnabled,
        place_all_variants: placeAllVariants,
        snaking_strategy: snakingStrategy,
        variant_contiguous: variantContiguous,
        allow_breaking_variant_sequencing: allowBreakingVariantSequencing,
        vertical_blocking_mode: ameVerticalBlockingMode,
        strict_rectangles: strictRectangles,
        ...(brandBlocksMinBays !== undefined && { brand_blocks_min_bays: brandBlocksMinBays }),
      },
      high_density_building_blocks_mode: {
        enabled: buildingBlocksEnabled,
        direction: buildingBlocksDirection,
        start_from: startFromType,
      },
      mudmap_level: mudmapLevel,
      optimize_on: optimizeOn,
      relax_factors: {
        space_mix: spaceMix,
        either_or: eitherOr,
        both_or_none: bothOrNone,
        prereq: preReq,
        shelf_item_count: shelfItemCount,
        fixed_notch_num: fixedNotchNum,
        finger_space: fingerSpace,
        units_not_enough_dos: unitsNotEnoughDos,
        units_not_enough_mos: unitsNotEnoughMos,
        units_too_many: unitsTooMany,
        facings_not_enough: facingsNotEnough,
        facings_too_many: facingsTooMany,
        rows_not_enough: rowsNotEnough,
        min_width: minWidth,
        min_distribution: minDistribution,
        width_slack: widthSlack,
        cdt1_contiguous: cdt1Contiguous,
        cdt2_contiguous: cdt2Contiguous,
        cdt3_contiguous: cdt3Contiguous,
        cdt1_order: cdt1Order,
        cdt2_order: cdt2Order,
        cdt3_order: cdt3Order,
        item_id_order: itemIdOrder,
        item_span: itemSpan,
        cdt1_span: cdt1Span,
        cdt2_span: cdt2Span,
        cdt3_span: cdt3Span,
        secondary_orientation: secondaryOrientation,
        shelf_segment_space: shelfSegmentSpace,
      },
      ...(mode !== "base" && {
        mudmap_generation_penalty_multiplier: {
          totalSpaceSlack,
          cdt0SectionsSpanning,
          cdt0ShelvesSpanning,
          cdt2SectionsSpanning,
          cdt2ShelvesSpanning,
          cdt0RoundingSlack,
          cdt2RoundingSlack,
          cdt1BreakingSequencing,
          cdt2BreakingSequencing,
          cdtSpaceMixSlack,
          cdt0ShelvesMisaligned,
          itemWidthSatisfied,
          cdt0ShelfSpaceMisalignment,
          cdt2ShelfSpaceMisalignment,
          verticalBlockShelfPresence,
          cdt0CellXMisalignment,
          cdt2CellXMisalignment,
        },
        // Base generation stands no pallets, so it carries no pallet rules.
        pallets: {
          max_products_per_pallet: maxProductsPerPallet,
          max_stack_height: maxStackHeight,
        },
      }),
      // Omitted, not defaulted, while the gate is off: the tab is hidden, so
      // persisting rules nobody can see or edit would hand them to whoever
      // switches the setting on later. Optional on the contract, and
      // `configTemplate.ts` already accepts a config without it.
      weight_rules: isShelfWeightEnabled
        ? {
            default_shelf_weight_limit: defaultShelfWeightLimit,
            heavy_rule: heavyRule,
            resolve_overweight_shelves: resolveOverweightShelves,
            swap_heavy_brands_below_max_height: swapHeavyBrandsBelowMaxHeight,
            swap_fill_floor_pct: swapFillFloorPct,
            // Not user-facing: the backend forces it on whenever the merchant
            // gate is enabled, since the report is the screen's only source of
            // shelf limits.
            report: false,
          }
        : undefined,
    } satisfies PspRules;
  };

  const withGenerationLock = async (run: () => Promise<void>) => {
    if (isGeneratingRef.current) {
      return;
    }

    isGeneratingRef.current = true;
    setIsGenerating(true);

    try {
      await run();
    } finally {
      isGeneratingRef.current = false;
      if (isMountedRef.current) {
        setIsGenerating(false);
      }
    }
  };

  const handleGenerationError = (error: unknown) => {
    toast({
      title: "Failed to start generation",
      error,
    });
  };

  const runBaseGeneration = async ({
    merchflowId,
    pog,
    s2SolutionId,
  }: StorePlanogramRulesModalBaseProps) => {
    const response = await postStartMerchflowBasePogApi(merchflowId, {
      s2_solution_id: s2SolutionId,
      s2_solution: pog,
      psp_rules: buildPspRules(),
    });

    if (!response?.base_pog_id) {
      return;
    }

    navigate(
      routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
        merchflowId,
        basePogId: response.base_pog_id,
      }),
    );
    closeModal();
  };

  const runStoreGeneration = async ({
    basePogId,
    merchflowId,
  }: StorePlanogramRulesModalStoreProps) => {
    const storePogTriggerId = copyToStores
      ? await postMerchflowStartStorePlanogramsCopyToStoresGenerationApi({
          base_pog_id: basePogId,
        })
      : await postMerchflowStartStorePlanogramsGenerationApi({
          base_pog_id: basePogId,
          psp_rules: buildPspRules(),
        });

    if (!storePogTriggerId) {
      return;
    }

    closeModal();

    const navigateToStorePlanograms = () => {
      navigate(
        routeToUrl(
          pages.merchflows_merchflowId_StoresAll,
          {
            merchflowId,
          },
          {
            store_pog_trigger_id: storePogTriggerId,
          },
        ),
      );
    };

    for (let attempt = 0; attempt < MAX_STORE_GENERATION_POLL_ATTEMPTS; attempt += 1) {
      const storeAllResponse = await getMerchflowStoresAllApi(merchflowId, {
        page: 1,
        size: 8,
        store_pog_trigger_id: storePogTriggerId,
        viewMode: "list",
      });

      if (storeAllResponse?.data?.length) {
        navigateToStorePlanograms();
        return;
      }

      if (attempt < MAX_STORE_GENERATION_POLL_ATTEMPTS - 1) {
        await wait(STORE_GENERATION_POLL_INTERVAL_MS);
      }
    }

    toast({
      title: "Generation is taking longer than expected",
      message: "You can check the status later on the Store Planograms page.",
      type: "WARNING",
    });
    navigateToStorePlanograms();
  };

  const startBaseGeneration = async () => {
    if (mode !== "base") {
      return;
    }

    await withGenerationLock(() => runBaseGeneration(props));
  };

  const startStoreGeneration = async () => {
    if (mode !== "store") {
      return;
    }

    await withGenerationLock(() => runStoreGeneration(props));
  };

  const startStoreGenerationAfterFavorite = async () => {
    if (mode !== "store") {
      return;
    }

    await withGenerationLock(async () => {
      await postMerchflowBasePog_basePogId_StatusApi({
        is_favourite: [props.basePogId],
      });
      await runStoreGeneration(props);
    });
  };

  const handleBaseGenerateClick = () => {
    startBaseGeneration().catch(handleGenerationError);
  };

  const closeModal = () => {
    _closeModal("ModalStorePlanogramRules");
  };

  const openCdtStudio = () => {
    if (props.mode !== "store") {
      return;
    }

    closeModal();
    navigate(
      withStudioWizard(
        routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
          merchflowId,
          basePogId: props.basePogId,
        }),
      ),
    );
  };

  const isBaseMode = mode === "base";
  // Either switch sends vertical blocking, so either one needs the finalized CDT.
  const isVerticalRequested = !copyToStores && (verticalBlockingMode || ameVerticalBlockingMode);
  const isVerticalBlocked = isVerticalBlockingWithoutRelease(
    studioBlockingState,
    isVerticalRequested,
  );
  // Finalize is mandatory where the Studio applies: store planograms are built from the rules it
  // ships, so a run from the rules waits for it. Copying the base to stores reads no rules.
  const isFinalizeRequired = studioBlockingState?.kind === "notFinalized" && !copyToStores;
  const isGeneratingWithoutStudio = isFinalizeRequired && !isVerticalBlocked;
  // Names the CDT choice as the CDT section does, so the two never disagree.
  const cdtSourceLabel = t(
    cdtSource === "CATEGORY_TREE"
      ? "planogramRules.cdtSource.categoryTree.label"
      : "planogramRules.cdtSource.storeProduct.label",
  );
  const blockingSummary =
    studioBlockingState === undefined ||
    studioBlockingState.kind === "studioUnavailable" ||
    copyToStores
      ? undefined
      : t("planogramRules.blocking.summary", {
          mode: t(
            isVerticalRequested
              ? "planogramRules.blocking.vertical"
              : "planogramRules.blocking.horizontal",
          ),
          cdtSource: cdtSourceLabel,
        });
  // Says what the default follows, so it still reads true after the switch is flipped.
  const verticalBlockingModeDescription =
    (studioBlockingState?.kind === "finalized" &&
      (studioBlockingState.wallBlocking === "vertical"
        ? t("planogramRules.blocking.followsStudioOn", {
            releaseId: studioBlockingState.releaseId,
          })
        : t("planogramRules.blocking.followsStudioOffRows", {
            releaseId: studioBlockingState.releaseId,
          }))) ||
    (studioBlockingState?.kind === "notFinalized" &&
      t("planogramRules.blocking.followsStudioOffNotFinalized")) ||
    undefined;
  const isUhdModeEnabled = highDensityEnabled || buildingBlocksEnabled;
  const weightTab: RulesTabOption[] = isShelfWeightEnabled
    ? [{ label: t("planogramRules.weightRules.tab"), value: "WEIGHT" }]
    : [];

  // Base planogram generation supplies its own mudmap (built from the S2
  // solution), so the pogger skips mudmap generation entirely and reads none of
  // the UHD tab's fields. Offering the tab there renders an empty pane.
  const uhdTab: RulesTabOption[] = isBaseMode
    ? []
    : [{ label: "Auto-Mudmap Engine (UHD Mode)", value: "UHD" }];

  const tabs: RulesTabOption[] = [
    { label: "General", value: "GENERAL" },
    ...uhdTab,
    { label: "Optimization Options", value: "OPTIMIZATION" },
    ...weightTab,
  ];

  const isStoreGenerateDisabled =
    isGenerating ||
    isVerticalBlocked ||
    isFinalizeRequired ||
    oConciergeReady === false ||
    (!copyToStores && (dividerHold !== null || !isOSettled)) ||
    (isShelfWeightEnabled && shelfCapOn && draftShelfCapGrams <= 0);
  const generateStores = () => {
    startStoreGeneration().catch(handleGenerationError);
  };

  const openStore = ({ store, storePogId }: OpenedStore) => {
    navigate(
      storePogId === undefined
        ? routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_, {
            merchflowId,
            storeCode: store,
          })
        : routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_, {
            merchflowId,
            storeCode: store,
            storePogId,
          }),
    );
    closeModal();
  };

  const openRun = (storePogTriggerId: number) => {
    navigate(
      routeToUrl(
        pages.merchflows_merchflowId_StoresAll,
        { merchflowId },
        { store_pog_trigger_id: storePogTriggerId },
      ),
    );
    closeModal();
  };

  const oFrontDoor =
    props.mode === "store"
      ? (rules: ReactNode) => (
          <OFrontDoor
            advanced={
              rules && <OTouchedContext.Provider value={oMarks}>{rules}</OTouchedContext.Provider>
            }
            canGenerate={canCreate}
            isGenerating={isGenerating}
            isGenerateDisabled={isStoreGenerateDisabled}
            generateTitle={(!copyToStores && dividerHold) || undefined}
            summary={blockingSummary}
            blockedReason={
              isVerticalBlocked
                ? t("planogramRules.blocking.verticalBlockedReason")
                : isFinalizeRequired
                  ? t("planogramRules.blocking.finalizeFirst")
                  : undefined
            }
            blockedAction={
              isFinalizeRequired
                ? { label: t("planogramRules.blocking.openStudio"), onClick: openCdtStudio }
                : undefined
            }
            copiesBase={copyToStores}
            onStopCopying={() => setCopyToStores(false)}
            asksFavourite={!props.isBasePogFavorited}
            onCancel={closeModal}
            onGenerate={generateStores}
            onFavouriteAndGenerate={() => {
              startStoreGenerationAfterFavorite().catch(handleGenerationError);
            }}
          >
            <ConfigCopilotPanel
              merchflowId={merchflowId}
              basePogId={props.basePogId}
              blocking={basePogBlocking}
              readRules={buildRulesForO}
              writeRules={applyPspRules}
              onAdvanced={() => setView("ADVANCED")}
              isShown={view === "O"}
              onSettled={setIsOSettled}
              onConciergeReady={setOConciergeReady}
              onMarks={setOMarks}
              pendingFix={props.oFix}
              onGenerate={canCreate && !isStoreGenerateDisabled ? generateStores : undefined}
              onOpenStore={openStore}
              onOpenRun={openRun}
              onOpenCdtStudio={openCdtStudio}
            />
          </OFrontDoor>
        )
      : undefined;

  return (
    <Modal
      title={isBaseMode ? "Base Planogram Rules" : t("configCopilot.generate")}
      name="ModalStorePlanogramRules"
      width={isBaseMode ? "500px" : "720px"}
      height={isBaseMode ? "700px" : "80vh"}
    >
      <AdvancedRules
        isShown={view === "ADVANCED"}
        onBackToO={props.mode === "store" ? () => setView("O") : undefined}
        inFront={oFrontDoor}
      >
        <Flex minHeight="0" flexGrow column gap="20px" padding="10px">
          {isGeneratingWithoutStudio && (
            <Notice
              isBlocking
              testId="rules-notice-without-studio"
              title={t("planogramRules.blocking.finalizeFirstTitle")}
              text={t("planogramRules.blocking.finalizeFirst")}
            >
              <Button color="primary" variant="inverted" onClick={openCdtStudio}>
                {t("planogramRules.blocking.openStudio")}
              </Button>
            </Notice>
          )}

          {isVerticalBlocked && (
            <Notice
              isBlocking
              testId="rules-notice-vertical-blocked"
              title={t("planogramRules.blocking.verticalBlockedTitle")}
              text={t("planogramRules.blocking.verticalBlockedText")}
            >
              <Button
                color="primary"
                variant="inverted"
                onClick={() => {
                  handleVerticalBlockingModeChange(false);
                }}
              >
                {t("planogramRules.blocking.stayHorizontal")}
              </Button>
              <Button color="primary" onClick={openCdtStudio}>
                {t("planogramRules.blocking.openStudio")}
              </Button>
            </Notice>
          )}

          {showEngineNeededNotice && (
            <Notice text="Auto-Mudmap is off. Vertical blocking remains enabled, but the planogram will be generated without a mudmap.">
              <Button
                color="primary"
                variant="inverted"
                onClick={() => {
                  handleVerticalBlockingModeChange(false);
                }}
              >
                Turn vertical blocking off
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  handleHighDensityEnabledChange(true);
                }}
              >
                Turn the engine on
              </Button>
            </Notice>
          )}

          <Flex column width="100%" data-tour="rules-tabs">
            <Tabs
              tabs={tabs}
              value={tab}
              setValue={setTab}
              isDisabled={copyToStores && !isBaseMode}
            />
          </Flex>

          <Scroller gutter="stable">
            {tab === "GENERAL" && (
              <GeneralPlanogramRules
                cdtSourceSummary={
                  props.mode === "store" && cdtSource === "CATEGORY_TREE" ? (
                    <CdtSourceSummary
                      merchflowId={props.merchflowId}
                      generation={{ storeCodes: props.storeCodes, hasRelease: hasCdtRelease }}
                    />
                  ) : undefined
                }
                isDisabled={copyToStores && !isBaseMode}
                dividersSection={
                  <DividerRules
                    policy={dividerPolicy}
                    setPolicy={setDividerPolicy}
                    basePog={dividerData.basePog}
                    products={dividerData.products}
                    unit={DEFAULT_LENGTH_UNIT}
                    levelNames={dividerData.levelNames}
                    loaderLibraryShelves={dividerData.libraryShelves}
                    fixtureDividerRows={dividerData.fixtureDividerRows}
                    isLoading={dividerData.isLoading}
                    isDisabled={copyToStores && !isBaseMode}
                    isFreeTextDisabled={isBaseMode}
                    baseStoreCode={dividerData.baseStoreCode}
                    basePogId={dividerData.basePogId}
                    interpret={tellOInterpret}
                    saveShelf={tellOSaveShelf}
                  />
                }
                // Fixture
                canAdjustShelfNotches={canAdjustShelfNotches}
                setCanAdjustShelfNotches={setCanAdjustShelfNotches}
                canAddOrDeleteShelves={canAddOrDeleteShelves}
                setCanAddOrDeleteShelves={setCanAddOrDeleteShelves}
                shelvesSameHeightAsBasePog={shelvesSameHeightAsBasePog}
                setShelvesSameHeightAsBasePog={setShelvesSameHeightAsBasePog}
                verticalOverlap={verticalOverlap}
                setVerticalOverlap={setVerticalOverlap}
                mergedShelfWidthTolerancePct={mergedShelfWidthTolerancePct}
                setMergedShelfWidthTolerancePct={setMergedShelfWidthTolerancePct}
                fingerSpace={fingerSpaceGap}
                setFingerSpace={setFingerSpaceGap}
                // Range
                canAddNonCoreRangeProducts={canAddNonCoreRangeProducts}
                setCanAddNonCoreRangeProducts={setCanAddNonCoreRangeProducts}
                canApplySqueezeToPlacedProducts={canApplySqueezeToPlacedProducts}
                setCanApplySqueezeToPlacedProducts={setCanApplySqueezeToPlacedProducts}
                excludeSlowMovers={excludeSlowMovers}
                setExcludeSlowMovers={chooseExcludeSlowMovers}
                minCandidateQuantity={minCandidateQuantity}
                setMinCandidateQuantity={setMinCandidateQuantity}
                isCdtSourceVisible={!isBaseMode}
                cdtSource={cdtSource}
                setCdtSource={setCdtSource}
                hasCdtRelease={hasCdtRelease}
                cdtDefaultReason={cdtDefaultReason}
                // Merchandising
                verticalBlockingMode={verticalBlockingMode}
                setVerticalBlockingMode={handleVerticalBlockingModeChange}
                verticalBlockingModeDescription={verticalBlockingModeDescription}
                canBreakCasesTraysIfCoreRangeNotFit={canBreakCasesTraysIfCoreRangeNotFit}
                setCanBreakCasesTraysIfCoreRangeNotFit={setCanBreakCasesTraysIfCoreRangeNotFit}
                canFillExtraSpaceNextToCasesTraysWithLooseProducts={
                  canFillExtraSpaceNextToCasesTraysWithLooseProducts
                }
                setCanFillExtraSpaceNextToCasesTraysWithLooseProducts={
                  setCanFillExtraSpaceNextToCasesTraysWithLooseProducts
                }
                stackToShelfTop={stackToShelfTop ?? false}
                setStackToShelfTop={setStackToShelfTop}
                verticalCapping={verticalCapping ?? false}
                setVerticalCapping={setVerticalCapping}
                stackPastMaxStack={stackPastMaxStack ?? false}
                setStackPastMaxStack={setStackPastMaxStack}
                verticalCappingPastMaxStack={verticalCappingPastMaxStack ?? false}
                setVerticalCappingPastMaxStack={setVerticalCappingPastMaxStack}
                howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays={
                  howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays
                }
                setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays={
                  setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays
                }
                leverageAlternativeOrientations={leverageAlternativeOrientations}
                setLeverageAlternativeOrientations={setLeverageAlternativeOrientations}
                extraFacingMarginalValue={extraFacingMarginalValue ?? 0}
                setExtraFacingMarginalValue={setExtraFacingMarginalValue}
                snaking={snaking}
                setSnaking={setSnaking}
                snakingCascadeCdtLevel={snakingCascadeCdtLevel}
                setSnakingCascadeCdtLevel={setSnakingCascadeCdtLevel}
                cdtStacking={cdtStacking}
                setCdtStacking={setCdtStacking}
                itemsSqueezeOption={itemsSqueezeOption}
                setItemsSqueezeOption={setItemsSqueezeOption}
              />
            )}

            {tab === "GENERAL" && !isBaseMode && (
              <Flex padding="20px 0 0">
                <PalletRules
                  isDisabled={copyToStores}
                  maxProductsPerPallet={maxProductsPerPallet}
                  setMaxProductsPerPallet={setMaxProductsPerPallet}
                  maxStackHeight={maxStackHeight}
                  setMaxStackHeight={setMaxStackHeight}
                />
              </Flex>
            )}

            {tab === "UHD" && (
              <>
                <UhdPlanogramRules
                  isDisabled={copyToStores && !isBaseMode}
                  merchflowId={merchflowId}
                  // High Density Mode
                  highDensityEnabled={highDensityEnabled}
                  setHighDensityEnabled={handleHighDensityEnabledChange}
                  placeAllVariants={placeAllVariants}
                  setPlaceAllVariants={setPlaceAllVariants}
                  snakingStrategy={snakingStrategy}
                  setSnakingStrategy={setSnakingStrategy}
                  variantContiguous={variantContiguous}
                  setVariantContiguous={setVariantContiguous}
                  allowBreakingVariantSequencing={allowBreakingVariantSequencing}
                  setAllowBreakingVariantSequencing={setAllowBreakingVariantSequencing}
                  ameVerticalBlockingMode={ameVerticalBlockingMode}
                  brandBlocksMinBays={brandBlocksMinBays}
                  setBrandBlocksMinBays={setBrandBlocksMinBays}
                  verticalSplitSection={verticalSplitSection}
                  setVerticalSplitSection={setVerticalSplitSection}
                  // High Density Building Blocks Mode
                  buildingBlocksEnabled={buildingBlocksEnabled}
                  setBuildingBlocksEnabled={setBuildingBlocksEnabled}
                  buildingBlocksDirection={buildingBlocksDirection}
                  setBuildingBlocksDirection={setBuildingBlocksDirection}
                  startFromType={startFromType}
                  setStartFromType={setStartFromType}
                />
                <MudmapOptimizationOptions
                  isDisabled={copyToStores || !isUhdModeEnabled}
                  totalSpaceSlack={totalSpaceSlack}
                  setTotalSpaceSlack={setTotalSpaceSlack}
                  cdt0SectionsSpanning={cdt0SectionsSpanning}
                  setCdt0SectionsSpanning={setCdt0SectionsSpanning}
                  cdt0ShelvesSpanning={cdt0ShelvesSpanning}
                  setCdt0ShelvesSpanning={setCdt0ShelvesSpanning}
                  cdt2SectionsSpanning={cdt2SectionsSpanning}
                  setCdt2SectionsSpanning={setCdt2SectionsSpanning}
                  cdt2ShelvesSpanning={cdt2ShelvesSpanning}
                  setCdt2ShelvesSpanning={setCdt2ShelvesSpanning}
                  cdt0RoundingSlack={cdt0RoundingSlack}
                  setCdt0RoundingSlack={setCdt0RoundingSlack}
                  cdt2RoundingSlack={cdt2RoundingSlack}
                  setCdt2RoundingSlack={setCdt2RoundingSlack}
                  cdt1BreakingSequencing={cdt1BreakingSequencing}
                  setCdt1BreakingSequencing={setCdt1BreakingSequencing}
                  cdt2BreakingSequencing={cdt2BreakingSequencing}
                  setCdt2BreakingSequencing={setCdt2BreakingSequencing}
                  cdtSpaceMixSlack={cdtSpaceMixSlack}
                  setCdtSpaceMixSlack={setCdtSpaceMixSlack}
                  cdt0ShelvesMisaligned={cdt0ShelvesMisaligned}
                  setCdt0ShelvesMisaligned={setCdt0ShelvesMisaligned}
                  itemWidthSatisfied={itemWidthSatisfied}
                  setItemWidthSatisfied={setItemWidthSatisfied}
                  cdt0ShelfSpaceMisalignment={cdt0ShelfSpaceMisalignment}
                  setCdt0ShelfSpaceMisalignment={setCdt0ShelfSpaceMisalignment}
                  cdt2ShelfSpaceMisalignment={cdt2ShelfSpaceMisalignment}
                  setCdt2ShelfSpaceMisalignment={setCdt2ShelfSpaceMisalignment}
                  verticalBlockShelfPresence={verticalBlockShelfPresence}
                  setVerticalBlockShelfPresence={setVerticalBlockShelfPresence}
                  cdt0CellXMisalignment={cdt0CellXMisalignment}
                  setCdt0CellXMisalignment={setCdt0CellXMisalignment}
                  cdt2CellXMisalignment={cdt2CellXMisalignment}
                  setCdt2CellXMisalignment={setCdt2CellXMisalignment}
                  onReset={resetMudmapOptimizationOptions}
                />
              </>
            )}

            {tab === "OPTIMIZATION" && (
              <OptimizationOptionsRules
                isDisabled={copyToStores && !isBaseMode}
                // UhdStorePropService sends fixed_notch_num as "inf" whenever
                // shelf notches cannot be adjusted; the base leg sends the
                // slider's value as-is.
                isFixedNotchNumPinned={!isBaseMode && !canAdjustShelfNotches}
                spaceMix={spaceMix}
                setSpaceMix={setSpaceMix}
                eitherOr={eitherOr}
                setEitherOr={setEitherOr}
                bothOrNone={bothOrNone}
                setBothOrNone={setBothOrNone}
                preReq={preReq}
                setPreReq={setPreReq}
                shelfItemCount={shelfItemCount}
                setShelfItemCount={setShelfItemCount}
                fixedNotchNum={fixedNotchNum}
                setFixedNotchNum={setFixedNotchNum}
                fingerSpace={fingerSpace}
                setFingerSpace={setFingerSpace}
                unitsNotEnoughDos={unitsNotEnoughDos}
                setUnitsNotEnoughDos={setUnitsNotEnoughDos}
                unitsNotEnoughMos={unitsNotEnoughMos}
                setUnitsNotEnoughMos={setUnitsNotEnoughMos}
                unitsTooMany={unitsTooMany}
                setUnitsTooMany={setUnitsTooMany}
                facingsNotEnough={facingsNotEnough}
                setFacingsNotEnough={setFacingsNotEnough}
                facingsTooMany={facingsTooMany}
                setFacingsTooMany={setFacingsTooMany}
                rowsNotEnough={rowsNotEnough}
                setRowsNotEnough={setRowsNotEnough}
                minWidth={minWidth}
                setMinWidth={setMinWidth}
                minDistribution={minDistribution}
                setMinDistribution={setMinDistribution}
                widthSlack={widthSlack}
                setWidthSlack={setWidthSlack}
                cdt1Contiguous={cdt1Contiguous}
                setCdt1Contiguous={setCdt1Contiguous}
                cdt2Contiguous={cdt2Contiguous}
                setCdt2Contiguous={setCdt2Contiguous}
                cdt3Contiguous={cdt3Contiguous}
                setCdt3Contiguous={setCdt3Contiguous}
                cdt1Order={cdt1Order}
                setCdt1Order={setCdt1Order}
                cdt2Order={cdt2Order}
                setCdt2Order={setCdt2Order}
                cdt3Order={cdt3Order}
                setCdt3Order={setCdt3Order}
                itemIdOrder={itemIdOrder}
                setItemIdOrder={setItemIdOrder}
                itemSpan={itemSpan}
                setItemSpan={setItemSpan}
                cdt1Span={cdt1Span}
                setCdt1Span={setCdt1Span}
                cdt2Span={cdt2Span}
                setCdt2Span={setCdt2Span}
                cdt3Span={cdt3Span}
                setCdt3Span={setCdt3Span}
                secondaryOrientation={secondaryOrientation}
                setSecondaryOrientation={setSecondaryOrientation}
                shelfSegmentSpace={shelfSegmentSpace}
                setShelfSegmentSpace={setShelfSegmentSpace}
              />
            )}

            {tab === "WEIGHT" && (
              <WeightRules
                isDisabled={copyToStores && !isBaseMode}
                defaultShelfWeightLimit={defaultShelfWeightLimit}
                setDefaultShelfWeightLimit={setDefaultShelfWeightLimit}
                draftShelfCapGrams={draftShelfCapGrams}
                setDraftShelfCapGrams={setDraftShelfCapGrams}
                shelfCapOn={shelfCapOn}
                setShelfCapOn={setShelfCapOn}
                heavyRule={heavyRule}
                setHeavyRule={setHeavyRule}
                resolveOverweightShelves={resolveOverweightShelves}
                setResolveOverweightShelves={setResolveOverweightShelves}
                swapHeavyBrandsBelowMaxHeight={swapHeavyBrandsBelowMaxHeight}
                setSwapHeavyBrandsBelowMaxHeight={setSwapHeavyBrandsBelowMaxHeight}
                swapFillFloorPct={swapFillFloorPct}
                setSwapFillFloorPct={setSwapFillFloorPct}
              />
            )}
          </Scroller>
        </Flex>

        <Flex
          column
          background={Color.lightGray}
          borderTop={`1px solid ${Color.lightGrayHover}`}
          padding="20px"
          gap="20px"
          data-tour="rules-footer"
        >
          <OTouchedRow path="mudmap_level">
            <PlanogramMudmapLevel
              mudmapLevel={mudmapLevel}
              setMudmapLevel={setMudmapLevel}
              isDisabled={copyToStores && !isBaseMode}
            />
          </OTouchedRow>

          <OTouchedRow path="optimize_on">
            <PlanogramOptimizeOn
              optimizeOn={optimizeOn}
              setOptimizeOn={setOptimizeOn}
              isDisabled={copyToStores && !isBaseMode}
            />
          </OTouchedRow>

          {/*
            Copying an existing base planogram out to stores is only meaningful
            on the store leg — runBaseGeneration ignores this flag entirely.
          */}
          {!isBaseMode && (
            <>
              <HorizontalDivider />

              <Toggle
                label="Copy Base Planogram to Stores"
                value={copyToStores}
                setValue={(checked) => {
                  setCopyToStores(checked);
                }}
              />
            </>
          )}

          <Flex width="100%" justify="between" align="center">
            <Flex gap="8px">
              <Button
                color="primary"
                variant="inverted"
                isDisabled={copyToStores && !isBaseMode}
                onClick={handleImportClick}
              >
                Import
              </Button>
              <Button
                color="primary"
                variant="inverted"
                isDisabled={(copyToStores && !isBaseMode) || isDividerRuleLoading}
                onClick={handleExportConfig}
              >
                Export
              </Button>
            </Flex>

            {isBaseMode && (
              <Flex gap="20px">
                <Button color="red" variant="inverted" onClick={closeModal} isDisabled={false}>
                  Cancel
                </Button>
                {canCreate && (
                  <Button
                    color="primary"
                    onClick={handleBaseGenerateClick}
                    isDisabled={
                      isGenerating ||
                      (!copyToStores && dividerHold !== null) ||
                      (isShelfWeightEnabled && shelfCapOn && draftShelfCapGrams <= 0)
                    }
                    title={(!copyToStores && dividerHold) || undefined}
                    isLoading={isGenerating}
                  >
                    Generate Base Planogram
                  </Button>
                )}
              </Flex>
            )}
          </Flex>

          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleImportConfig}
          />
        </Flex>
      </AdvancedRules>
    </Modal>
  );
};
